import type { z } from 'zod';
import { logger } from './logger';

/**
 * Безопасная обёртка над localStorage: доступ к хранилищу может бросить исключение
 * (приватный режим, запрет cookies), значения из хранилища — недоверенные данные.
 * Прямая работа с localStorage в слайсах запрещена — только через этот модуль.
 */
const PREFIX = 'gwine-admin:';

function getStore(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

export const safeStorage = {
  get(key: string): string | null {
    try {
      return getStore()?.getItem(PREFIX + key) ?? null;
    } catch (error) {
      logger.warn('storage: read failed', { key, error: String(error) });
      return null;
    }
  },

  set(key: string, value: string): void {
    try {
      getStore()?.setItem(PREFIX + key, value);
    } catch (error) {
      logger.warn('storage: write failed', { key, error: String(error) });
    }
  },

  remove(key: string): void {
    try {
      getStore()?.removeItem(PREFIX + key);
    } catch (error) {
      logger.warn('storage: remove failed', { key, error: String(error) });
    }
  },

  /** Чтение JSON с проверкой схемой; невалидное значение удаляется. */
  getJson<T>(key: string, schema: z.ZodType<T>): T | null {
    const raw = safeStorage.get(key);
    if (raw === null) return null;
    try {
      const parsed = schema.safeParse(JSON.parse(raw));
      if (parsed.success) return parsed.data;
    } catch {
      // невалидный JSON — ниже значение удаляется
    }
    safeStorage.remove(key);
    return null;
  },

  setJson(key: string, value: unknown): void {
    safeStorage.set(key, JSON.stringify(value));
  },
};
